import { AssetSlot } from '@atlas/ui-components'

export interface CampaignMockupSlotView {
	slot: 'story' | 'feed' | 'reel' | 'print'
	label: string
	aspectRatioLabel: string
	exampleCaption?: string
}

export interface CampaignMockupSlotsProps {
	/** Exactly four fixed slots, supplied as props. */
	slots: readonly CampaignMockupSlotView[]
	/**
	 * Fill a slot from disk. Omit to leave the slots read-only — the campaign
	 * page passes it only where the viewer may add assets.
	 */
	onUpload?: (slot: CampaignMockupSlotView['slot'], files: File[]) => void
	/** Fill a slot from the library instead of uploading. Omit to hide the action. */
	onBrowse?: (slot: CampaignMockupSlotView['slot']) => void
}

/** Approved descriptive title shown inside each slot, keyed by the fixed slot id — a static local lookup, not a prop. */
const SLOT_TITLES: Record<CampaignMockupSlotView['slot'], string> = {
	story: 'Phone story mockup',
	feed: 'Feed post mockup',
	reel: 'Reel device mockup',
	print: 'Print poster mockup',
}

/**
 * The campaign's four fixed mockup slots.
 *
 * Each is a real slot rather than decoration: it can be filled by upload or by
 * picking an existing library asset, and which paths are offered is the caller's
 * choice — a read-only viewer passes neither handler and gets the same four
 * labelled shapes it always had.
 *
 * The slots are fixed and never derived from the campaign's files: nothing is
 * indexed, matched, or fallback-mapped into one. A slot shows what the caller
 * explicitly put there.
 */
export function CampaignMockupSlots({ slots, onUpload, onBrowse }: CampaignMockupSlotsProps) {
	return (
		<section aria-label="Mockup slots" className="grid grid-cols-2 gap-3 sm:grid-cols-4">
			{slots.map((slot) => (
				<AssetSlot
					key={slot.slot}
					label={SLOT_TITLES[slot.slot]}
					caption={
						<>
							{`${slot.label} · ${slot.aspectRatioLabel}`}
							{slot.exampleCaption && <span className="block text-ds-text-muted">{slot.exampleCaption}</span>}
						</>
					}
					accept="image/*"
					onFiles={onUpload && ((files) => onUpload(slot.slot, files))}
					onBrowse={onBrowse && (() => onBrowse(slot.slot))}
				/>
			))}
		</section>
	)
}
